import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';
import SubscriptionModal from '../components/SubscriptionModal';
import toast from 'react-hot-toast';

const BARTER_FEE = 10;
const CONDITIONS = [
  { value: 'new', label: 'New' },
  { value: 'like_new', label: 'Like New' },
  { value: 'excellent', label: 'Excellent' },
  { value: 'very_good', label: 'Very Good' },
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' }
];

export default function CreateBarterListingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [step, setStep] = useState(0);
  const [publishing, setPublishing] = useState(false);
  const [categories, setCategories] = useState([]);
  const [images, setImages] = useState([]);
  const [showSubscription, setShowSubscription] = useState(false);

  const [form, setForm] = useState({
    categoryId: '',
    title: '',
    description: '',
    condition: '',
    brand: '',
    model: '',
    year: '',
    locationCity: user?.locationCity || '',
    locationState: user?.locationState || '',
    locationZip: ''
  });

  const [errors, setErrors] = useState({});

  useState(() => {
    API.get('/categories').then(r => setCategories(r.data.categories)).catch(() => {});
  });

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const onDrop = useCallback(async (files) => {
    for (const file of files.slice(0, 5 - images.length)) {
      const fd = new FormData();
      fd.append('image', file);
      try {
        const { data } = await API.post('/upload/listing-image', fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setImages(prev => [...prev, { ...data, preview: URL.createObjectURL(file) }]);
      } catch (err) {
        toast.error('Failed to upload image');
      }
    }
  }, [images]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    maxSize: 10 * 1024 * 1024
  });

  const validateStep = () => {
    const e = {};
    if (step === 0) {
      if (!form.title.trim()) e.title = 'Title required';
      if (!form.description.trim()) e.description = 'Description required';
      if (!form.categoryId) e.categoryId = 'Category required';
      if (!form.condition) e.condition = 'Condition required';
    }
    if (step === 1) {
      if (!images.length) e.images = 'At least 1 image required';
    }
    if (step === 2) {
      if (!form.locationCity) e.locationCity = 'City required';
      if (!form.locationState) e.locationState = 'State required';
    }
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleNext = () => {
    if (validateStep()) setStep(s => s + 1);
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const { data } = await API.post('/barters/listings', {
        ...form,
        imageIds: images.map(i => i.id)
      });

      toast.success('Listing created!');
      setTimeout(() => {
        navigate('/barter');
      }, 1500);
    } catch (err) {
      if (err.response?.data?.error === 'subscription_required') {
        setShowSubscription(true);
      } else {
        toast.error(err.response?.data?.error || 'Failed to create listing');
      }
      setPublishing(false);
    }
  };

  const steps = ['Details', 'Photos', 'Location'];

  return (
    <div style={{ maxWidth: 700, margin: '0 auto', padding: '40px 48px 80px' }}>
      <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 700, marginBottom: 8 }}>
        🔄 Create Barter Listing
      </h1>
      <p style={{ fontSize: 14, color: 'var(--text-soft)', marginBottom: 32 }}>
        Flat fee: <strong>${BARTER_FEE}</strong> • No commission on trades
      </p>

      {/* Step indicator */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 40, justifyContent: 'center' }}>
        {steps.map((s, i) => (
          <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: i < step ? 'var(--cherry)' : i === step ? 'var(--cherry)' : 'var(--border)',
                color: i <= step ? 'white' : 'var(--text-soft)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: 13
              }}
            >
              {i < step ? '✓' : i + 1}
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, color: i === step ? 'var(--cherry)' : 'var(--text-soft)' }}>
              {s}
            </span>
            {i < steps.length - 1 && (
              <div style={{ width: 20, height: 2, background: i < step ? 'var(--cherry)' : 'var(--border)', marginLeft: 4 }} />
            )}
          </div>
        ))}
      </div>

      {/* Step 0 - Details */}
      {step === 0 && (
        <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 24, marginBottom: 24 }}>
          <div className="form-group">
            <label className="form-label">Title <span className="required">*</span></label>
            <input
              className={`form-input ${errors.title ? 'error' : ''}`}
              placeholder="What are you trading?"
              value={form.title}
              onChange={e => f('title', e.target.value)}
              required
            />
            {errors.title && <div className="form-error">{errors.title}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">Description <span className="required">*</span></label>
            <textarea
              className={`form-textarea ${errors.description ? 'error' : ''}`}
              placeholder="Describe the item, condition, and what you're looking for..."
              value={form.description}
              onChange={e => f('description', e.target.value)}
              style={{ minHeight: 120 }}
              required
            />
            {errors.description && <div className="form-error">{errors.description}</div>}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Category <span className="required">*</span></label>
              <select
                className={`form-select ${errors.categoryId ? 'error' : ''}`}
                value={form.categoryId}
                onChange={e => f('categoryId', e.target.value)}
              >
                <option value="">Select category</option>
                {categories.map(c => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
              {errors.categoryId && <div className="form-error">{errors.categoryId}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">Condition <span className="required">*</span></label>
              <select
                className={`form-select ${errors.condition ? 'error' : ''}`}
                value={form.condition}
                onChange={e => f('condition', e.target.value)}
              >
                <option value="">Select condition</option>
                {CONDITIONS.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
              {errors.condition && <div className="form-error">{errors.condition}</div>}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Brand</label>
              <input className="form-input" placeholder="e.g., Sony" value={form.brand} onChange={e => f('brand', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Model</label>
              <input className="form-input" placeholder="e.g., WH-1000XM5" value={form.model} onChange={e => f('model', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Year</label>
              <input className="form-input" placeholder="2023" value={form.year} onChange={e => f('year', e.target.value)} />
            </div>
          </div>
        </div>
      )}

      {/* Step 1 - Photos */}
      {step === 1 && (
        <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 24, marginBottom: 24 }}>
          <label className="form-label" style={{ marginBottom: 12 }}>
            Photos (up to 5) <span className="required">*</span>
          </label>

          <div
            {...getRootProps()}
            style={{
              border: '2px dashed var(--border)',
              borderRadius: 10,
              padding: 40,
              textAlign: 'center',
              cursor: 'pointer',
              background: isDragActive ? 'var(--cream)' : 'white',
              marginBottom: 20
            }}
          >
            <input {...getInputProps()} />
            <div style={{ fontSize: 32, marginBottom: 12 }}>📸</div>
            <p style={{ fontSize: 14, color: 'var(--text-mid)' }}>
              Drag photos here or click to browse
            </p>
            <p style={{ fontSize: 12, color: 'var(--text-soft)' }}>
              Max 10MB per image
            </p>
          </div>

          {errors.images && <div className="form-error">{errors.images}</div>}

          {images.length > 0 && (
            <div>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>
                {images.length} image{images.length !== 1 ? 's' : ''} uploaded
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {images.map((img, i) => (
                  <div key={i} style={{ position: 'relative' }}>
                    <img
                      src={img.preview}
                      alt=""
                      style={{ width: 80, height: 80, borderRadius: 8, objectFit: 'cover' }}
                    />
                    <button
                      onClick={() => setImages(p => p.filter((_, idx) => idx !== i))}
                      style={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        background: 'var(--cherry)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: 24,
                        height: 24,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 14
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 2 - Location */}
      {step === 2 && (
        <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 24, marginBottom: 24 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">City <span className="required">*</span></label>
              <input
                className={`form-input ${errors.locationCity ? 'error' : ''}`}
                value={form.locationCity}
                onChange={e => f('locationCity', e.target.value)}
                required
              />
              {errors.locationCity && <div className="form-error">{errors.locationCity}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">State <span className="required">*</span></label>
              <input
                className={`form-input ${errors.locationState ? 'error' : ''}`}
                value={form.locationState}
                onChange={e => f('locationState', e.target.value)}
                placeholder="CA"
                required
              />
              {errors.locationState && <div className="form-error">{errors.locationState}</div>}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">ZIP Code</label>
            <input
              className="form-input"
              value={form.locationZip}
              onChange={e => f('locationZip', e.target.value)}
              placeholder="90210"
            />
          </div>
        </div>
      )}

      {/* Navigation */}
      <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between' }}>
        {step > 0 && (
          <button className="btn btn-ghost" onClick={() => setStep(s => s - 1)}>
            ← Back
          </button>
        )}
        {step < steps.length - 1 ? (
          <button
            className="btn btn-cherry"
            style={{ marginLeft: 'auto' }}
            onClick={handleNext}
          >
            Continue →
          </button>
        ) : (
          <button
            className="btn btn-cherry btn-lg"
            style={{ marginLeft: 'auto' }}
            onClick={handlePublish}
            disabled={publishing}
          >
            {publishing ? <><span className="spinner" /> Publishing...</> : '🚀 Create Listing ($10)'}
          </button>
        )}
      </div>

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={showSubscription}
        onClose={() => setShowSubscription(false)}
        onSuccess={() => {
          setShowSubscription(false);
          toast.success('Subscription activated! Try creating your listing again.');
        }}
        mode="list"
      />
    </div>
  );
}
